import type { RootState } from '@/store';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  fetchColumnWidths,
  saveTableColumnWidths,
  selectTableColumnWidths,
} from '@/store/slices/reportingV2/columnWidthsSlice';
import { useEffect, useRef, useState } from 'react';

const MIN_WIDTH = 60;
const MAX_WIDTH = 800;
const SAVE_DEBOUNCE_MS = 500;

/**
 * Drag-to-resize + per-user persistence for a table's columns (flank#4819).
 *
 * `widths` is an OVERRIDE map, not every column's width: a column absent from
 * it hasn't been resized, so the caller keeps using its own existing default
 * (a `%` layout width, a fixed inline width, or nothing at all) — this is
 * additive on top of each table's current sizing rather than a rewrite of it,
 * since several of these tables' default widths are already tuned to
 * redistribute around whichever columns permissions currently hide (#4716).
 *
 * `startResize` takes the dragged cell's current rendered width (read from
 * the DOM by the caller) rather than a precomputed default, so it works the
 * same whether the column started at a `%` width or an auto-sized one.
 */
export const useResizableColumns = (tableKey: string) => {
  const dispatch = useAppDispatch();
  const savedWidths = useAppSelector(selectTableColumnWidths(tableKey));
  const status = useAppSelector((state: RootState) => state.columnWidths.status);

  const [widths, setWidths] = useState<Record<string, number>>({});
  const widthsRef = useRef(widths);
  useEffect(() => {
    widthsRef.current = widths;
  });

  useEffect(() => {
    dispatch(fetchColumnWidths());
  }, [dispatch]);

  // A drag that completes before the fetch resolves must survive the re-seed
  // below, the same way columnWidthsSlice's own fulfilled reducer already
  // lets in-flight Redux state win over the fetched snapshot (review, Lucas —
  // local state wasn't covered the same way). Reset per tableKey so switching
  // tables still takes the new table's fetched snapshot outright.
  const hasLocalEditRef = useRef(false);
  useEffect(() => {
    hasLocalEditRef.current = false;
  }, [tableKey]);

  useEffect(() => {
    if (status !== 'loaded') return;
    setWidths(prev => (hasLocalEditRef.current ? { ...savedWidths, ...prev } : savedWidths));
    // savedWidths/dispatch are stable per tableKey; re-seeding on `status` alone
    // (idle -> loaded) is what applies the fetched widths once they arrive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey, status]);

  const dragState = useRef<{ columnKey: string; startX: number; startWidth: number } | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const handleMove = (event: PointerEvent) => {
      const drag = dragState.current;
      if (!drag) return;
      const next = Math.min(
        MAX_WIDTH,
        Math.max(MIN_WIDTH, Math.round(drag.startWidth + (event.clientX - drag.startX))),
      );
      setWidths(prev => ({ ...prev, [drag.columnKey]: next }));
    };
    const handleUp = () => {
      if (!dragState.current) return;
      dragState.current = null;
      hasLocalEditRef.current = true;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        dispatch(saveTableColumnWidths({ tableKey, widths: widthsRef.current }));
      }, SAVE_DEBOUNCE_MS);
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      // A pending save must be flushed here, not dropped: this cleanup runs on
      // unmount AND on every tableKey change, so switching tabs within the
      // debounce window silently lost the resize that was the point of this
      // ticket (review, Lucas). `tableKey` is this effect run's own closed-over
      // value, so the flush still targets the table the drag actually happened on.
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = undefined;
        dispatch(saveTableColumnWidths({ tableKey, widths: widthsRef.current }));
      }
    };
  }, [dispatch, tableKey]);

  const startResize = (columnKey: string, startX: number, startWidth: number) => {
    dragState.current = { columnKey, startX, startWidth: startWidth || MIN_WIDTH };
  };

  return { widths, startResize };
};
